/**
 * DateFactory
 *
 * 日付オブジェクトの生成を一元管理するファクトリ
 * UI層でのnew Date()直接生成を禁止し、ドメイン層完結原則を遵守
 *
 * @example
 * // ❌ 禁止: UI層での直接生成
 * const now = new Date();
 *
 * // ✅ 推奨: ファクトリ経由で生成
 * const now = DateFactory.now();
 */
export const DateFactory = {
  /**
   * 現在時刻を取得
   *
   * @returns 現在の日時
   * @example
   * const currentTime = DateFactory.now();
   */
  now(): Date {
    return new Date();
  },

  /**
   * ISO 8601形式の文字列から日付を生成
   *
   * @param isoString - ISO 8601形式の日付文字列
   * @returns 日付オブジェクト
   * @example
   * const date = DateFactory.fromISOString('2024-09-22T08:00:00');
   */
  fromISOString(isoString: string): Date {
    return new Date(isoString);
  },

  /**
   * ミリ秒タイムスタンプから日付を生成
   *
   * @param timestamp - ミリ秒単位のタイムスタンプ
   * @returns 日付オブジェクト
   * @example
   * const date = DateFactory.fromTimestamp(1695369600000);
   */
  fromTimestamp(timestamp: number): Date {
    return new Date(timestamp);
  },

  /**
   * オフセット付き日付を生成（現在時刻からの相対時刻）
   *
   * @param offsetMs - 現在時刻からのオフセット（ミリ秒）
   * @returns 日付オブジェクト
   * @example
   * // 5分前
   * const fiveMinutesAgo = DateFactory.withOffset(-1000 * 60 * 5);
   *
   * // 1時間後
   * const oneHourLater = DateFactory.withOffset(1000 * 60 * 60);
   */
  withOffset(offsetMs: number): Date {
    return new Date(Date.now() + offsetMs);
  },

  /**
   * 日付範囲を生成
   *
   * @param params - 日付範囲パラメータ
   * @param params.startOffset - 開始日時のオフセット（ミリ秒）
   * @param params.endOffset - 終了日時のオフセット（ミリ秒）
   * @returns 開始日時と終了日時のオブジェクト
   * @example
   * // 過去7日間
   * const last7Days = DateFactory.createDateRange({
   *   startOffset: -1000 * 60 * 60 * 24 * 7,  // 7日前
   *   endOffset: 0                             // 現在
   * });
   */
  createDateRange(params: {
    readonly startOffset: number;
    readonly endOffset: number;
  }): { readonly startDate: Date; readonly endDate: Date } {
    const now = Date.now();
    return {
      startDate: new Date(now + params.startOffset),
      endDate: new Date(now + params.endOffset)
    };
  },

  /**
   * 固定された日付範囲を生成
   *
   * @param params - 日付範囲パラメータ
   * @param params.start - 開始日時（ISO文字列またはタイムスタンプ）
   * @param params.end - 終了日時（ISO文字列またはタイムスタンプ）
   * @returns 開始日時と終了日時のオブジェクト
   * @example
   * const range = DateFactory.createFixedDateRange({
   *   start: '2024-09-01T00:00:00',
   *   end: '2024-09-30T23:59:59'
   * });
   */
  createFixedDateRange(params: {
    readonly start: string | number;
    readonly end: string | number;
  }): { readonly startDate: Date; readonly endDate: Date } {
    return {
      startDate: typeof params.start === 'string'
        ? new Date(params.start)
        : new Date(params.start),
      endDate: typeof params.end === 'string'
        ? new Date(params.end)
        : new Date(params.end)
    };
  },

  /**
   * 日数単位でオフセットされた日付を生成
   *
   * @param days - 日数（負数で過去、正数で未来）
   * @returns 日付オブジェクト
   * @example
   * const yesterday = DateFactory.daysAgo(1);
   * const tomorrow = DateFactory.daysAgo(-1);
   */
  daysAgo(days: number): Date {
    const millisecondsPerDay = 1000 * 60 * 60 * 24;
    return new Date(Date.now() - (days * millisecondsPerDay));
  },

  /**
   * 時間単位でオフセットされた日付を生成
   *
   * @param hours - 時間数（負数で過去、正数で未来）
   * @returns 日付オブジェクト
   * @example
   * const oneHourAgo = DateFactory.hoursAgo(1);
   * const twoHoursLater = DateFactory.hoursAgo(-2);
   */
  hoursAgo(hours: number): Date {
    const millisecondsPerHour = 1000 * 60 * 60;
    return new Date(Date.now() - (hours * millisecondsPerHour));
  },

  /**
   * 分単位でオフセットされた日付を生成
   *
   * @param minutes - 分数（負数で過去、正数で未来）
   * @returns 日付オブジェクト
   * @example
   * const fiveMinutesAgo = DateFactory.minutesAgo(5);
   */
  minutesAgo(minutes: number): Date {
    const millisecondsPerMinute = 1000 * 60;
    return new Date(Date.now() - (minutes * millisecondsPerMinute));
  }
};
